/**
 * Per-form draft store, backed by `localStorage`.
 *
 * Drafts never leave the device. Each form has its own `formKey` (e.g.
 * `"dynamic-links:create"`); calls are scoped to that key so two forms can't
 * collide. Storage layout is wrapped with a schema version so we can migrate
 * later without breaking existing devices.
 */

const VERSION = 1;
const MAX_DRAFTS_PER_KEY = 20;

export interface Draft<T> {
  id: string;
  name: string;
  values: T;
  createdAt: number;
  updatedAt: number;
}

interface StoredDrafts<T> {
  v: number;
  drafts: Draft<T>[];
}

function storageKey(formKey: string): string {
  return `drafts:${formKey}`;
}

function safeStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readAll<T>(formKey: string): Draft<T>[] {
  const ls = safeStorage();
  if (!ls) return [];
  try {
    const raw = ls.getItem(storageKey(formKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredDrafts<T> | unknown;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      (parsed as StoredDrafts<T>).v !== VERSION ||
      !Array.isArray((parsed as StoredDrafts<T>).drafts)
    ) {
      return [];
    }
    return (parsed as StoredDrafts<T>).drafts;
  } catch {
    return [];
  }
}

/**
 * Same-tab change channel. The browser's `storage` event only fires in OTHER
 * tabs, so we publish a custom event for the tab that just wrote.
 */
const CHANGE_EVENT = "drafts-store:change";

function emitChange(formKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { formKey } }),
    );
  } catch {
    // ignore (older browsers without CustomEvent constructor)
  }
}

function writeAll<T>(formKey: string, drafts: Draft<T>[]): void {
  const ls = safeStorage();
  if (!ls) throw new Error("Local storage is not available.");
  const payload: StoredDrafts<T> = { v: VERSION, drafts };
  try {
    ls.setItem(storageKey(formKey), JSON.stringify(payload));
    emitChange(formKey);
  } catch (e) {
    if (e instanceof Error && /quota/i.test(e.name + e.message)) {
      throw new Error("Out of device storage. Delete a draft and try again.");
    }
    throw e;
  }
}

/** Subscribe to drafts changes for a given form key (same-tab + cross-tab). */
export function subscribeToDraftsChange(
  formKey: string,
  onChange: () => void,
): () => void {
  const onLocal = (e: Event) => {
    const detail = (e as CustomEvent<{ formKey: string }>).detail;
    if (detail?.formKey === formKey) onChange();
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === storageKey(formKey)) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onLocal);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onLocal);
    window.removeEventListener("storage", onStorage);
  };
}

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** Newest first. */
export function listDrafts<T>(formKey: string): Draft<T>[] {
  return readAll<T>(formKey).sort((a, b) => b.updatedAt - a.updatedAt);
}

export function saveDraft<T>(
  formKey: string,
  name: string,
  values: T,
): Draft<T> {
  const all = readAll<T>(formKey);
  const now = Date.now();
  const draft: Draft<T> = {
    id: newId(),
    name: name.trim() || "Untitled draft",
    values,
    createdAt: now,
    updatedAt: now,
  };
  // Keep only the newest N — drop the oldest first.
  const next = [draft, ...all]
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, MAX_DRAFTS_PER_KEY);
  writeAll(formKey, next);
  return draft;
}

export function renameDraft<T>(
  formKey: string,
  id: string,
  name: string,
): void {
  const all = readAll<T>(formKey);
  const next = all.map((d) =>
    d.id === id
      ? { ...d, name: name.trim() || d.name, updatedAt: Date.now() }
      : d,
  );
  writeAll(formKey, next);
}

export function deleteDraft<T>(formKey: string, id: string): void {
  const all = readAll<T>(formKey);
  writeAll(
    formKey,
    all.filter((d) => d.id !== id),
  );
}

export function clearDrafts(formKey: string): void {
  const ls = safeStorage();
  if (!ls) return;
  try {
    ls.removeItem(storageKey(formKey));
  } catch {
    // ignore
  }
}

/** Storage event filter — useful for cross-tab sync. */
export function isDraftsStorageEvent(
  event: StorageEvent,
  formKey: string,
): boolean {
  return event.key === storageKey(formKey);
}

export interface PromptAndSaveResult<T> {
  ok: boolean;
  /** Set when the user cancelled the prompt. */
  cancelled?: boolean;
  draft?: Draft<T>;
  error?: string;
}

/**
 * Prompt for a name with `window.prompt` and save the snapshot. Convenience
 * helper for callers that want a one-line save without their own modal.
 */
export function promptAndSaveDraft<T>(
  formKey: string,
  values: T,
  opts: { suggestedName?: string; promptMessage?: string } = {},
): PromptAndSaveResult<T> {
  const suggested = (opts.suggestedName ?? "").trim() || "Untitled draft";
  const message = opts.promptMessage ?? "Name this draft:";
  const name =
    typeof window === "undefined"
      ? suggested
      : window.prompt(message, suggested);
  if (name == null) return { ok: false, cancelled: true };
  try {
    const draft = saveDraft<T>(formKey, name, values);
    return { ok: true, draft };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Could not save draft",
    };
  }
}

export const DRAFT_LIMITS = {
  perKey: MAX_DRAFTS_PER_KEY,
} as const;
